import { useEffect, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { LuShieldCheck } from 'react-icons/lu'
import { subscribeToRefreshState } from '../api/client'
import { useT } from '../i18n'
import { fonts, mineral, shadows } from '../theme'
import KunturMark from './brand/KunturMark'

/**
 * Aviso mientras se renueva el token. Es una pausa breve, no un error: tarjeta blanca
 * sobre un velo claro (la página se intuye detrás), el cóndor de la marca y UN anillo
 * que gira mientras dura la renovación. El velo bloquea clics para que nadie dispare
 * acciones con credenciales a medio renovar.
 */
export default function SessionRefreshOverlay() {
  const { t } = useT()
  const [visible, setVisible] = useState(false)

  useEffect(() => subscribeToRefreshState(setVisible), [])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="session-refresh"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, background: 'rgba(255, 255, 255, 0.55)',
            backdropFilter: 'blur(8px) saturate(120%)', WebkitBackdropFilter: 'blur(8px) saturate(120%)',
          }}
          role="status"
          aria-live="polite"
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            style={{ width: '100%', maxWidth: 380 }}
          >
            <Box position="relative" bg={mineral.surface} borderRadius="22px" border={`1px solid ${mineral.line}`}
              boxShadow={shadows.lg} overflow="hidden">
              {/* Línea de progreso indeterminada: la renovación está en curso */}
              <Box position="absolute" top={0} left={0} right={0} h="3px" bg={mineral.sunk} overflow="hidden">
                <motion.div
                  animate={{ x: ['-100%', '250%'] }}
                  transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ width: '40%', height: '100%', background: `linear-gradient(90deg, transparent, ${mineral.ocre}, transparent)` }}
                />
              </Box>

              <Flex direction="column" align="center" px={8} pt={9} pb={7} gap={4}>
                {/* El cóndor dentro de un anillo que gira */}
                <Box position="relative" w="96px" h="96px">
                  <svg width="96" height="96" viewBox="0 0 96 96" style={{ position: 'absolute', inset: 0 }}>
                    <circle cx="48" cy="48" r="44" fill="none" stroke={mineral.ocreSoft} strokeWidth="4" />
                  </svg>
                  <motion.svg width="96" height="96" viewBox="0 0 96 96" style={{ position: 'absolute', inset: 0 }}
                    animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}>
                    <circle cx="48" cy="48" r="44" fill="none" stroke={mineral.ocre} strokeWidth="4" strokeLinecap="round"
                      strokeDasharray="70 207" />
                  </motion.svg>
                  <Flex position="absolute" inset="12px" borderRadius="full" bg={mineral.ocreSoft} align="center" justify="center" color={mineral.ocreDeep}>
                    <KunturMark width={50} />
                  </Flex>
                </Box>

                <Box textAlign="center">
                  <Flex align="center" justify="center" gap={2}>
                    <Box color={mineral.turquesa} display="flex"><LuShieldCheck size={17} /></Box>
                    <Text fontFamily={fonts.display} fontWeight={800} fontSize="19px" letterSpacing="-0.02em" color={mineral.ink}>
                      {t('session.refreshing')}
                    </Text>
                  </Flex>
                  <Text fontSize="13.5px" color={mineral.ink3} mt={1.5} maxW="290px" lineHeight="1.55">
                    {t('session.refreshingHint')}
                  </Text>
                </Box>
              </Flex>
            </Box>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
